import { App } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';
import { registerDirtyCheck } from '../lib/unsaved';

/**
 * Warns before navigating away (or closing/refreshing the tab) while an editor
 * holds unsaved changes. `shouldBlock` is a FUNCTION, not a boolean, so the
 * blocker reads the latest dirty value at navigation time — this lets an editor
 * flip its dirty ref to false right before its OWN post-save `navigate()` (which
 * fires in the same tick, before React re-renders) without being blocked.
 *
 * NOTE: react-router allows only ONE active blocker at a time, so mount at most
 * one guard on screen. Use `UnsavedChangesGuard` (a render-only component) so it
 * can live inside a single JSX branch and is torn down when that branch isn't shown.
 */
export function useUnsavedChangesPrompt(shouldBlock: () => boolean): void {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const shouldBlockRef = useRef(shouldBlock);
  shouldBlockRef.current = shouldBlock;

  const blocker = useBlocker(useCallback(() => shouldBlockRef.current(), []));

  // Only one confirm dialog per blocked transition (blocker identity changes on
  // re-render, so guard with a ref rather than firing on every render).
  const promptOpen = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked' || promptOpen.current) return;
    promptOpen.current = true;
    modal.confirm({
      title: t('common.unsavedTitle'),
      content: t('common.unsavedText'),
      okText: t('common.unsavedLeave'),
      cancelText: t('common.unsavedStay'),
      okButtonProps: { danger: true },
      onOk: () => {
        promptOpen.current = false;
        blocker.proceed?.();
      },
      onCancel: () => {
        promptOpen.current = false;
        blocker.reset?.();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  // Let non-React code (pwa.ts update reload) know an editor is dirty.
  useEffect(() => registerDirtyCheck(() => shouldBlockRef.current()), []);

  // Native prompt on refresh / tab close while dirty.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent): void => {
      if (!shouldBlockRef.current()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}

/** Render-only guard: mount inside exactly one editor branch to keep a single
 * active blocker. Returns nothing. */
export function UnsavedChangesGuard({ shouldBlock }: { shouldBlock: () => boolean }): null {
  useUnsavedChangesPrompt(shouldBlock);
  return null;
}
